import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { translations } from "@/i18n/translations";

const LanguageContext = createContext({ lang: "en", setLang: () => {}, t: (k) => k });

// Non-sensitive UI preference key — safe in localStorage
const STORAGE_KEY = "rolink-lang";

function getNested(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    // 1) URL parameter override (e.g. ?lang=hu) – highest priority
    try {
      const params = new URLSearchParams(window.location.search);
      const urlLang = params.get("lang");
      if (urlLang === "en" || urlLang === "hu") return urlLang;
    } catch (_) {
      // URL parsing failed — fall through
    }
    // 2) Stored user preference
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "en" || stored === "hu") return stored;
    } catch (_) {
      // localStorage may throw in private mode — fall through
    }
    // 3) Auto-detect Hungarian browser locale
    if (typeof navigator !== "undefined" && navigator.language && navigator.language.toLowerCase().startsWith("hu")) {
      return "hu";
    }
    return "en";
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, lang);
    } catch (_) {
      // Storage unavailable — language still applies for this session
    }
    if (typeof window !== "undefined" && window.history && window.history.replaceState) {
      try {
        const url = new URL(window.location.href);
        if (url.searchParams.get("lang") !== lang) {
          url.searchParams.set("lang", lang);
          window.history.replaceState({}, "", url.toString());
        }
      } catch (_) {
        // URL update is non-critical — content already reflects current lang
      }
    }
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", lang);
      const titles = {
        en: "RoLink Biotechnology | Pandemic Preparedness Platform",
        hu: "RoLink Biotechnology | Védelmi képesség a Járványok Korában",
      };
      const descs = {
        en: "RoLink Biotechnology develops proprietary UV-C viral inactivation technology enabling safer diagnostics, vaccine development, biodefense, and pandemic preparedness while preserving viral structure.",
        hu: "A RoLink Biotechnology szabadalmaztatott UV-C vírusinaktiválási technológiát fejleszt biztonságosabb diagnosztikához, vakcinafejlesztéshez, biodefenz alkalmazásokhoz és pandémiás felkészültséghez — a vírus szerkezetének megőrzése mellett.",
      };
      document.title = titles[lang] || titles.en;
      const descTag = document.querySelector('meta[name="description"]');
      if (descTag) descTag.setAttribute("content", descs[lang] || descs.en);
      const ogLocale = document.querySelector('meta[property="og:locale"]');
      if (ogLocale) ogLocale.setAttribute("content", lang === "hu" ? "hu_HU" : "en_US");
      const ogTitle = document.querySelector('meta[property="og:title"]');
      if (ogTitle) ogTitle.setAttribute("content", titles[lang] || titles.en);
      const ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute("content", descs[lang] || descs.en);
    }
  }, [lang]);

  const setLang = useCallback((l) => {
    if (l === "en" || l === "hu") setLangState(l);
  }, []);

  const t = useCallback((key) => {
    const dict = translations[lang] || translations.en;
    const value = getNested(dict, key);
    if (value !== undefined) return value;
    const enValue = getNested(translations.en, key);
    return enValue !== undefined ? enValue : key;
  }, [lang]);

  const ctxValue = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return (
    <LanguageContext.Provider value={ctxValue}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}

export function useT() {
  return useContext(LanguageContext).t;
}
